import { useEffect, useMemo } from 'react';

import {
  giftBrandSellerDeliveryPopulate,
  listGiftBrandSellerDeliveries,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { listGiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.controller';
import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { listGiftSupplySettlements } from '@shared/api/giftSupplySettlement/giftSupplySettlement.controller';
import {
  GiftSettlementStatus,
  GiftSupplySettlement,
} from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

import {
  computeSettlementLines,
  ceilWon,
  SettlementLine,
} from '../../giftSeller/utils/settlement.utils';
import { getKstDateParts } from '../utils/settlement.utils';

// ─────────────────────────────────────────────
// useGiftBrandSettlementData
// ─────────────────────────────────────────────

/** `useGiftBrandSettlementData`가 반환하는 값의 타입 */
export interface GiftBrandSettlementData {
  /** 전체 배송완료 내역 기반 정산 라인 */
  deliveredLines: SettlementLine[];
  /** 배송완료 내역 로딩 중 여부 */
  deliveredLoading: boolean;
  /** 정산 예정 = 배송완료 & 정산 확정(Draft)인 배송 기반 정산 라인 */
  pendingLines: SettlementLine[];
  /** 정산 예정 로딩 중 여부 */
  pendingLoading: boolean;
  /** 정산 완료 = 지급완료(Paid) 정산 문서 목록 */
  historyItems: GiftSupplySettlement[] | undefined;
  /** 정산 완료 내역 로딩 중 여부 */
  historyLoading: boolean;
  /** 공급사 본인의 정산정보 (관리자 뷰에서는 undefined) */
  mySettlementInfo: SettlementInfo | undefined;
  /**
   * 업체 id → `SettlementInfo` 조회 함수.
   * 정산완료 라인의 surcharge 분해(소량추가단가/택배비)에 사용한다.
   */
  settlementByBrand: (brandId?: string) => SettlementInfo | undefined;
  /** 다음 정산 예정일 정보. settlementDay 미설정 시 undefined */
  nextSettlement:
    | { year: number; month: number; day: number; dDay: number }
    | undefined;
}

/**
 * 공급사/관리자 정산 페이지에 필요한 모든 서버 데이터를 페치하고
 * 파생 state를 계산해 반환하는 커스텀 훅.
 *
 * @param role - 현재 인증된 사용자의 역할 (Admin 또는 GiftBrandSeller)
 */
export function useGiftBrandSettlementData(
  role: LowerRole.Admin | LowerRole.GiftBrandSeller,
): GiftBrandSettlementData {
  const isAdminView = role === LowerRole.Admin;

  // ===== 배송완료 내역 =====
  const [{ loading: deliveredLoading, value: deliveredDeliveries }, fetchDelivered] =
    useAsyncFn(
      async () =>
        listGiftBrandSellerDeliveries(
          // 배송완료 & 아직 업체정산되지 않은(supplySettlement=null) 건만 조회
          { deliveryStatus: GiftDeliveryStatus.Delivered, supplySettlement: null },
          { 'shipping.deliveredAt': -1 },
          role,
        ),
      [role],
    );

  // ===== 정산 예정 = 배송완료 & 정산 확정(Draft) 정산 문서의 배송 라인 =====
  // Draft 정산 문서를 가져와 각 라인의 giftDelivery를 populate → 배송 목록으로 펼친다.
  const [{ loading: pendingLoading, value: pendingDocs }, fetchPending] = useAsyncFn(
    async () =>
      listGiftSupplySettlements(
        { status: GiftSettlementStatus.Draft },
        [
          { path: 'giftItemBrand', select: 'name' },
          { path: 'lines.giftDelivery', populate: giftBrandSellerDeliveryPopulate },
        ],
        { sort: { year: -1, month: -1 } },
        role,
      ),
    [role],
  );

  // ===== 정산 완료 내역 = 전체(정산 확정 Draft + 지급완료 Paid) 정산 문서 =====
  // 상세 모달의 배송요청일/배송완료일/택배사/송장번호 표시용으로 lines.giftDelivery populate
  const [{ loading: historyLoading, value: historyDocs }, fetchHistory] = useAsyncFn(
    async () =>
      listGiftSupplySettlements(
        {},
        [
          { path: 'giftItemBrand', select: 'name' },
          { path: 'lines.giftDelivery', populate: giftBrandSellerDeliveryPopulate },
        ],
        { sort: { year: -1, month: -1 } },
        role,
      ),
    [role],
  );

  // ===== 정산정보 (공급사 본인 또는 전체 브랜드) =====
  const [{ value: myBrands }, fetchMyBrands] = useAsyncFn(
    async () => listGiftItemBrand(role),
    [role],
  );

  useEffect(() => {
    fetchDelivered();
    fetchPending();
    fetchHistory();
    fetchMyBrands();
  }, [fetchDelivered, fetchPending, fetchHistory, fetchMyBrands]);

  const mySettlementInfo = isAdminView ? undefined : myBrands?.[0]?.settlementInfo;
  const settlementDay = mySettlementInfo?.settlementDay;

  /** 업체 id → 정산정보 조회 함수 */
  const settlementByBrand = useMemo(() => {
    const map = new Map<string, SettlementInfo | undefined>();
    (myBrands ?? []).forEach((b) => map.set(b._id, b.settlementInfo));
    return (brandId?: string) => (brandId ? map.get(brandId) : undefined);
  }, [myBrands]);

  const deliveredLines = useMemo(
    () => computeSettlementLines(deliveredDeliveries ?? [], settlementByBrand),
    [deliveredDeliveries, settlementByBrand],
  );

  // 정산 예정 = Draft 정산 문서의 라인에서 populate된 배송을 펼쳐 재계산.
  // (백엔드 settle 시 택배비 계산이 누락돼 동결 surcharge가 부정확하므로,
  //  동결값 대신 현재 settlementInfo 기준으로 프론트에서 재계산해 올바른 금액을 표시한다.)
  // 취소(Canceled)된 배송은 정산 예정에서 제외한다.
  const pendingLines = useMemo(() => {
    const deliveries = (pendingDocs ?? [])
      .flatMap((doc) => doc.lines.map((line) => line.giftDelivery))
      .filter(
        (d): d is GiftDeliveryForAdmin =>
          !!d &&
          typeof d === 'object' &&
          d.deliveryStatus !== GiftDeliveryStatus.Canceled,
      );
    return computeSettlementLines(deliveries, settlementByBrand);
  }, [pendingDocs, settlementByBrand]);

  // 정산 완료 내역 = 정산 문서. 취소(Canceled)된 배송 라인은 제외하고
  // 건수·정산금액(totalAmount)을 남은 라인 기준으로 재집계한다.
  // (라인 금액 자체는 동결값을 그대로 쓰고, 취소 라인만 합계에서 뺀다.)
  // 모든 라인이 취소되어 빈 문서가 되면 목록에서 숨긴다.
  const historyItems = useMemo<GiftSupplySettlement[] | undefined>(() => {
    if (!historyDocs) return undefined;
    return historyDocs
      .map((doc) => {
        const lines = doc.lines.filter((line) => {
          const d = line.giftDelivery;
          // populate 안 된(문자열) 경우 상태를 알 수 없으니 유지, 객체면 취소 제외
          return !(
            typeof d === 'object' && d?.deliveryStatus === GiftDeliveryStatus.Canceled
          );
        });
        const totalAmount = ceilWon(lines.reduce((sum, l) => sum + l.lineAmount, 0));
        return { ...doc, lines, totalAmount };
      })
      .filter((doc) => doc.lines.length > 0);
  }, [historyDocs]);

  /** KST 기준 다음 정산일 계산 */
  const nextSettlement = useMemo(() => {
    if (typeof settlementDay !== 'number') return undefined;
    const { year: todayYear, month: todayMonth, day: todayDay } = getKstDateParts();

    let targetYear = todayYear;
    let targetMonth = todayMonth;
    if (todayDay > settlementDay) {
      targetMonth += 1;
      if (targetMonth > 12) {
        targetMonth = 1;
        targetYear += 1;
      }
    }

    const todayUtc = Date.UTC(todayYear, todayMonth - 1, todayDay);
    const nextUtc = Date.UTC(targetYear, targetMonth - 1, settlementDay);
    const dDay = Math.round((nextUtc - todayUtc) / 86400000);

    return { year: targetYear, month: targetMonth, day: settlementDay, dDay };
  }, [settlementDay]);

  return {
    deliveredLines,
    deliveredLoading,
    pendingLines,
    pendingLoading,
    historyItems,
    historyLoading,
    mySettlementInfo,
    settlementByBrand,
    nextSettlement,
  };
}
